import { ChangeEvent, useRef } from 'react';
import './TextInputComponent.scss';
import useAutosizeTextArea from './textAreaResize';

type IValueInput = string | number;

interface ITextInput {
	defaultValue?: string;
	disabled?: boolean;
	error?: boolean;
	helperText?: React.ReactElement;
	onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
	placeholder: string;
	required?: boolean;
	iconStart?: any;
	iconEnd?: any;
	link?: string;
	errorLink?: boolean;
	textStart?: string;
	textEnd?: string;
	name: string;
	value: IValueInput;
	type?: 'text' | 'number' | 'email' | 'password' | 'textarea';
	errorIcon?: any;
	errorHelperText?: React.ReactElement;
	commaNumeric?: boolean;
	allowSymbols?: boolean;
}

const TextInputComponent: React.FC<ITextInput> = ({
	defaultValue,
	disabled,
	error,
	helperText,
	placeholder,
	required,
	iconStart,
	iconEnd,
	link,
	name,
	onChange,
	textEnd = '',
	textStart = '',
	value,
	type = 'text',
	errorIcon,
	errorHelperText,
	commaNumeric,
	allowSymbols
}) => {
	const showStart = iconStart || textStart.length > 0;
	const showEnd = iconEnd || textEnd.length > 0;
	const textAreaRef = useRef<HTMLTextAreaElement>(null);
	useAutosizeTextArea(textAreaRef.current, value);
	let displayValue: string;
	let inputElementTypeForRender = type;

	if (commaNumeric) {
		let cleanValue = value;
		if (!Number(value)) {
			cleanValue = value.toString().replace(/\D/g, '');
		}
		displayValue = Number(cleanValue).toLocaleString('en-US');
		inputElementTypeForRender = 'text';
	} else {
		displayValue = String(value);
	}
	const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		let newValue = e.target.value;
		if (allowSymbols === false) {
			newValue = newValue.replace(/[^a-zA-ZÀ-ÿ\s]/g, '');
		}
		if (onChange) {
			const event = {
				...e,
				target: {
					...e.target,
					value: newValue,
					name: name
				}
			} as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
			onChange(event);
		}
	};

	return (
		<div className={`${showStart ? 'with-start-text' : ' '} text-input-container`}>
			<div className={`${error ? 'error' : disabled ? 'disabled' : 'default'} main-container`}>
				<div
					className={`${disabled ? 'disabled' : ''} ${error ? 'error' : ''} input-form-container`}
				>
					{showStart && (
						<div className='label-icon-start'>
							<div className='text-start'>{textStart}</div>
							{iconStart}
						</div>
					)}
					{type !== 'textarea' ? (
						<input
							type={inputElementTypeForRender}
							className='input-form'
							disabled={disabled}
							name={name}
							placeholder={placeholder}
							value={displayValue}
							onChange={handleChange}
						></input>
					) : (
						<textarea
							className='input-form'
							disabled={disabled}
							name={name}
							placeholder={placeholder}
							value={displayValue}
							ref={textAreaRef}
							onChange={handleChange}
						></textarea>
					)}

					{error ? <i className='ri-error-warning-fill' /> : null}
					{showEnd && (
						<div className='label-icon-end'>
							<div>{textEnd}</div>
							{iconEnd}
						</div>
					)}
				</div>
			</div>
			{helperText ? <div className='helper-text'>{helperText}</div> : null}
			{error ? (
				<>
					<div className='error-message'>
						<div>{errorHelperText}</div>
					</div>
				</>
			) : null}
		</div>
	);
};

export default TextInputComponent;
